import { formatDateTime } from '@/lib/utils'
import { borderRadius, colors, fontWeights } from '@/theme'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Button, Menu, MenuItem, Typography } from '@mui/material'
import { formatDistance } from 'date-fns'
import { useState } from 'react'
import type { BlockedStatusOption, Stage } from './types'

interface StatusHeaderProps {
  startDate: Date | null
  currentStage: Stage | null
  statusOptions: readonly string[]
  blockedStatusOptions?: readonly BlockedStatusOption[]
  onStatusSelect: (status: string) => void
  readOnly?: boolean
}

export default function StatusHeader({
  startDate,
  currentStage,
  statusOptions,
  blockedStatusOptions,
  onStatusSelect,
  readOnly
}: StatusHeaderProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const menuOpen = Boolean(anchorEl)
  const blockedReasonMap = new Map((blockedStatusOptions ?? []).map(option => [option.status, option.reason]))
  const availableStatuses = statusOptions.filter(status => !blockedReasonMap.has(status))
  const blockedStatuses = statusOptions.filter(status => blockedReasonMap.has(status))

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)
  const handleMenuClose = () => setAnchorEl(null)
  const handleStatusClick = (status: string) => {
    setAnchorEl(null)
    onStatusSelect(status)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        p: 2,
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.sm,
        mb: 3,
        bgcolor: colors.background.surface
      }}
    >
      <Box>
        <Typography variant='fieldLabel' sx={{ display: 'block', mb: 0.5 }}>
          Started
        </Typography>
        <Typography variant='fieldValue' sx={{ fontWeight: fontWeights.semibold }}>
          {startDate ? formatDateTime(startDate) : '-'}
        </Typography>
      </Box>

      <Box>
        <Typography variant='fieldLabel' sx={{ display: 'block', mb: 0.5 }}>
          Time open
        </Typography>
        <Typography variant='fieldValue' sx={{ fontWeight: fontWeights.semibold }}>
          {startDate ? formatDistance(startDate, new Date()) : '-'}
        </Typography>
      </Box>

      {!readOnly && (
        <Box sx={{ ml: 'auto' }}>
          <Button
            variant='link'
            onClick={handleMenuClick}
            endIcon={
              <KeyboardArrowDownIcon
                sx={{ transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              />
            }
          >
            Change Status
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 220,
                  borderRadius: borderRadius.sm,
                  border: `1px solid ${colors.border.default}`
                }
              }
            }}
          >
            {availableStatuses.map(status => {
              const isCurrent = currentStage?.label === status
              return (
                <MenuItem
                  key={status}
                  onClick={() => handleStatusClick(status)}
                  selected={isCurrent}
                  disabled={isCurrent}
                  sx={{
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    bgcolor: isCurrent ? colors.brand.primaryLight : undefined
                  }}
                >
                  <Typography variant='body1' color={isCurrent ? 'primary' : 'textPrimary'}>
                    {status}
                    {isCurrent && (
                      <Typography component='span' variant='smallText' sx={{ ml: 1 }}>
                        (Current)
                      </Typography>
                    )}
                  </Typography>
                  {isCurrent && <CheckIcon fontSize='small' color='primary' />}
                </MenuItem>
              )
            })}

            {blockedStatuses.map(status => {
              const isCurrent = currentStage?.label === status
              const reason = blockedReasonMap.get(status)
              return (
                <MenuItem
                  key={status}
                  disabled
                  title={reason}
                  sx={{
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    bgcolor: isCurrent ? colors.brand.primaryLight : undefined
                  }}
                >
                  <Typography variant='body1' color={isCurrent ? 'primary' : 'textSecondary'}>
                    {status}
                    {reason && (
                      <Typography component='span' variant='smallText' sx={{ ml: 1 }}>
                        ({reason})
                      </Typography>
                    )}
                  </Typography>
                  {isCurrent && <CheckIcon fontSize='small' color='primary' />}
                </MenuItem>
              )
            })}
          </Menu>
        </Box>
      )}
    </Box>
  )
}
